import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import IconWithText from '@/components/ui/IconWithText/IconWithText';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';
import EditChildDialog from '@/components/children/EditChildDialog/EditChildDialog';
import AddChildrenForm from '@/pages/Supporteds/AddChildrenPopup/AddChildrenForm';
import { ChildBasicDetailsDtoIn } from '@/models/in/ChildBasicDetailsDtoIn';
import { ChildDtoOut } from '@/models/out/ChildDtoOut';
import { ChildDtoIn } from '@/models/in/ChildDtoIn';
import { AddChildrenDtoOut } from '@/models/out/AddChildrenDtoOut';
import { ManagementSupportedDtoIn } from '@/models/in/ManagementSupportedDtoIn';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { formatDateLocale, isBirthDateInAllowedRange, MIN_PLAUSIBLE_YEAR } from '@/helpers/dateHelper';
import approvedSupport from '@/assets/icons/approved-support.svg';
import id2Icon from '@/assets/icons/id2.svg';
import calendar from '@/assets/icons/calendar.svg';
import home from '@/assets/icons/home.svg';
import editIcon from '@/assets/icons/edit.svg';
import styles from './SupportedChildrenDialog.module.css';

interface SupportedChildrenDialogProps {
  supported: ManagementSupportedDtoIn;
  open: boolean;
  onClose: () => void;
  onRefreshList?: () => void;
}

const childrenApiUrl = (supportedId: number) => `management/supporteds/${supportedId}/children`;

/**
 * The children of a supported, as managed from the management area: view the existing ones, edit a
 * child's details, and add new ones. Children belong to the parent contact rather than to a fund,
 * so no fund has to be picked here. A child added here starts out pending approval, exactly like
 * one added by a coordinator.
 */
const SupportedChildrenDialog = ({ supported, open, onClose, onRefreshList }: SupportedChildrenDialogProps) => {
  const { t, i18n } = useTranslation();
  const [newChildren, setNewChildren] = useState<ChildDtoOut[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [childToEdit, setChildToEdit] = useState<ChildBasicDetailsDtoIn | null>(null);

  const { data: children, isLoading } = useGetAuthApi<ChildBasicDetailsDtoIn[]>(
    open ? childrenApiUrl(supported.id) : ''
  );

  const { mutateAsync: addChildren, isPending: isSubmitting } = usePostAuthApi<ChildDtoIn[], AddChildrenDtoOut>(
    childrenApiUrl(supported.id)
  );

  // Start with one empty child when opening, and clear everything when closing
  useEffect(() => {
    if (open) {
      setNewChildren((current) => (current.length === 0
        ? [{ firstName: '', israeliId: '', birthDate: undefined, livesWithSupported: true }]
        : current));
    } else {
      setNewChildren([]);
      setDocuments([]);
      setChildToEdit(null);
    }
  }, [open]);

  const refreshChildren = () => {
    queryClient.invalidateQueries({ queryKey: [childrenApiUrl(supported.id)] });
    onRefreshList?.();
  };

  const handleSubmit = async () => {
    const filledChildren = newChildren.filter(
      (child) => child.firstName || child.israeliId || child.birthDate
    );

    if (filledChildren.length === 0) {
      alert(t('pleaseAddAtLeastOneChild'));
      return;
    }

    const errors: string[] = [];

    if (filledChildren.some((child) => !child.firstName || !child.israeliId || !child.birthDate)) {
      errors.push(t('pleaseCompleteAllRequiredFields'));
    }

    // A birth date centuries off is as wrong as a missing one, and it hides itself in the
    // children table afterwards - stop it here (SPR-1179).
    filledChildren
      .filter((child) => !isBirthDateInAllowedRange(child.birthDate))
      .forEach((child) => {
        errors.push(`${child.firstName || t('child')}: ${t('birthDateTooEarly', { year: MIN_PLAUSIBLE_YEAR })}`);
      });

    filledChildren
      .filter((child) => child.israeliId && !isValidIsraeliID(child.israeliId))
      .forEach((child) => errors.push(`${t('israeliId')} ${child.israeliId} ${t('invalid')}`));

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    try {
      const convertedDocuments = await Promise.all(
        documents.map((file) => FileReaderHelper.fileToDocumentDtoOut(file))
      );

      await addChildren({
        children: filledChildren.map((child) => ({
          firstName: child.firstName,
          israeliId: child.israeliId,
          birthDate: child.birthDate,
          livesWithSupported: child.livesWithSupported,
        })),
        documents: convertedDocuments.flat(),
      });

      alert(t('childrenAddedSuccessfully'));
      setNewChildren([{ firstName: '', israeliId: '', birthDate: undefined, livesWithSupported: true }]);
      setDocuments([]);
      refreshChildren();
    } catch (error) {
      const errorMessage = (error as { response?: { data?: string } })?.response?.data
        || (error as Error)?.message
        || t('errorAddingChildren');
      alert(errorMessage);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={`${t('children')} - ${supported.firstName} ${supported.lastName}`}
    >
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {/* Existing children, each editable */}
          <div className="flex flex-col gap-4">
            <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('existingChildren')}</h4>

            {isLoading ? (
              <p className={styles.loading}>{t('loading')}...</p>
            ) : !children || children.length === 0 ? (
              <div className="py-4">
                <span className="body-lr-regular text-(--font-body-2)">{t('noChildrenYet')}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {children.map((child) => (
                  <div key={child.id} className={styles.childRow}>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 items-center">
                        <div className="h-[15px] border-l-2 border-(--border-tertiary-strong)"></div>
                        <span className="body-lr-semibold text-(--font-body-1)">
                          {child.firstName} {child.lastName}
                        </span>
                        <button
                          type="button"
                          title={t('childrenManagement.editChildDialog.title')}
                          aria-label={t('childrenManagement.editChildDialog.title')}
                          className={styles.iconAction}
                          onClick={() => setChildToEdit(child)}
                        >
                          <TintedIcon icon={editIcon} size={18} />
                        </button>
                      </div>
                      <div className="flex flex-col">
                        {child.status === RelationshipStatus.Approved ? (
                          <IconWithText icon={approvedSupport} text={t(`childStatus.${child.status}`)} />
                        ) : (
                          <span className="body-lr-regular text-(--font-body-1)">{t(`childStatus.${child.status}`)}</span>
                        )}
                        <IconWithText icon={id2Icon} text={child.israeliId} />
                        {child.birthDate && (
                          <IconWithText icon={calendar} text={formatDateLocale(child.birthDate, i18n.language)} />
                        )}
                        {child.livesWithSupported && (
                          <IconWithText icon={home} text={t('livesWithSupported')} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.divider} />

          {/* Add new children */}
          <AddChildrenForm
            children={newChildren}
            onChildrenChange={setNewChildren}
            documents={documents}
            onDocumentsChange={setDocuments}
          />
        </div>

        <div className={styles.footer}>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('saveChildren')}
          </Button>
        </div>
      </div>

      {childToEdit && (
        <EditChildDialog
          open={!!childToEdit}
          onClose={() => setChildToEdit(null)}
          child={{
            childId: childToEdit.id,
            firstName: childToEdit.firstName,
            lastName: childToEdit.lastName,
            israeliId: childToEdit.israeliId,
            birthDate: childToEdit.birthDate,
          }}
          onSuccess={refreshChildren}
        />
      )}
    </MainDialogLayout>
  );
};

export default SupportedChildrenDialog;
